/**
 * Oct 20 (Issue 03B, FD-01) — Move Vehicle: one dialog for every standalone move (bay ↔ spot, spot ↔ spot,
 * bay ↔ bay, general lot, on site unknown, off site), on phone, tablet and desktop alike.
 *
 * Built only from existing pieces: readGuidedStartContext / guidedBayChoices (bays + occupancy, as Guided Start),
 * readParkingSpotChoices (spots + occupancy, as the Stop's parking question), moveVehicle → moveRequestArgs →
 * move_vehicle_location (the ONE canonical authority). Two steps — choose, then confirm. A refusal is shown in
 * the server's words and the choices are re-read; nothing is retried or substituted.
 *
 * Oct 20 addendum L5: the confirmation asks WHO physically moved the vehicle — "I moved it" or "recorded on behalf
 * of <employee>". Nothing is pre-selected: the recorder is the mover only when they say so.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  moveVehicle, readGuidedStartContext, readParkingSpotChoices, readVehicleMoverCandidates, type VehicleMoverCandidate,
} from '@/lib/location/api';
import type { GuidedStartContext } from '@/lib/timer/interaction/guidedStart';
import {
  MOVE_GUARANTEES, moveConfirmSentence, moveDestinations, moverAttestationOf,
  type MoveDestination, type MoveDestinationGroup, type MoverChoice,
} from '@/lib/location/moveDestinations';
import { MOVER_ROLE_WORDS } from '@/lib/tickets/locationCustodyComposition';

export interface FohMoveVehicleDialogProps {
  open: boolean;
  ticketId: string | null;
  shopId: string;
  onClose: () => void;
  /** After a confirmed move (the caller refreshes boards). */
  onMoved: (ticketId: string, result: { replayed: boolean; capacityNotVerified: boolean }) => void;
}

/** One id per confirmed submission: a retried request is answered, not repeated (AVLL request_id replay). */
function newRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `move-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const GROUP_LABEL: Record<MoveDestinationGroup, string> = { bay: 'Bays', spot: 'Parking spots', other: 'Other' };

export function FohMoveVehicleDialog({ open, ticketId, shopId, onClose, onMoved }: FohMoveVehicleDialogProps) {
  const [ctx, setCtx] = useState<GuidedStartContext | null>(null);
  const [dests, setDests] = useState<MoveDestination[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [mover, setMover] = useState<MoverChoice | null>(null);
  const [candidates, setCandidates] = useState<VehicleMoverCandidate[] | null>(null);
  const [candidatesError, setCandidatesError] = useState(false);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [c, spots] = await Promise.all([
        readGuidedStartContext({ ticketId, shopId, legacyParked: false }),
        readParkingSpotChoices(shopId, ticketId),
      ]);
      setCtx(c);
      setDests(moveDestinations(c, spots));
    } catch (e) {
      console.error('[FohMoveVehicleDialog] could not read the floor', e);
      setLoadError('Could not read where vehicles are right now. Nothing was moved.');
    } finally {
      setLoading(false);
    }
  }, [ticketId, shopId]);

  useEffect(() => {
    if (!open) return;
    setSelected(null); setConfirming(false); setRefusal(null); setMover(null);
    void load();
  }, [open, load]);

  // The employee picker is read only when someone is being recorded on another's behalf.
  useEffect(() => {
    if (!open || mover?.kind !== 'onBehalf' || candidates !== null) return;
    let live = true;
    readVehicleMoverCandidates(shopId)
      .then((c) => { if (live) { setCandidates(c); setCandidatesError(false); } })
      .catch((e) => { console.error('[FohMoveVehicleDialog] could not read employees', e); if (live) { setCandidates([]); setCandidatesError(true); } });
    return () => { live = false; };
  }, [open, mover?.kind, candidates, shopId]);
  useEffect(() => { if (!open) { setCandidates(null); setCandidatesError(false); } }, [open]);

  const chosen = useMemo(() => dests.find((d) => d.key === selected) ?? null, [dests, selected]);
  const attestation = moverAttestationOf(mover);

  const submit = async () => {
    if (!ticketId || !chosen || !attestation || busy) return;
    setBusy(true);
    setRefusal(null);
    try {
      const r = await moveVehicle(ticketId, chosen.target, { requestId: newRequestId(), mover: attestation });
      if (r.ok) {
        onMoved(ticketId, { replayed: r.replayed, capacityNotVerified: r.capacityNotVerified === true });
        onClose();
        return;
      }
      // The server's own words (occupied, running work, locked out …). Re-read: the floor changed.
      setRefusal('reason' in r ? r.reason : 'The move was refused.');
      setConfirming(false);
      setSelected(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const groups: MoveDestinationGroup[] = ['bay', 'spot', 'other'];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="foh-move-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>{confirming ? 'Confirm move' : 'Move vehicle'}</DialogTitle>
          <DialogDescription>
            {confirming && ctx && chosen ? moveConfirmSentence(ctx, chosen) : MOVE_GUARANTEES}
          </DialogDescription>
        </DialogHeader>

        {refusal && (
          <p role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive"
             data-testid="foh-move-refusal">
            {refusal} Nothing was moved.
          </p>
        )}
        {loadError && <p role="alert" className="text-sm text-destructive">{loadError}</p>}

        {confirming && (
          <fieldset className="space-y-2" data-testid="foh-move-mover">
            <legend className="text-sm font-medium">Who moved the vehicle?</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Who moved the vehicle?">
              <button type="button" role="radio" aria-checked={mover?.kind === 'self'} disabled={busy}
                data-testid="foh-move-mover-self" onClick={() => setMover({ kind: 'self' })}
                className={cn('min-h-11 rounded-md border p-2 text-left text-sm',
                  mover?.kind === 'self' ? 'border-primary bg-primary/10' : 'hover:bg-muted')}>
                I moved it myself
              </button>
              <button type="button" role="radio" aria-checked={mover?.kind === 'onBehalf'} disabled={busy}
                data-testid="foh-move-mover-other" onClick={() => setMover({ kind: 'onBehalf', moverUserId: null })}
                className={cn('min-h-11 rounded-md border p-2 text-left text-sm',
                  mover?.kind === 'onBehalf' ? 'border-primary bg-primary/10' : 'hover:bg-muted')}>
                Another employee moved it — I'm recording it for them
              </button>
            </div>
            {mover?.kind === 'onBehalf' && (
              <div className="space-y-1">
                <label htmlFor="foh-move-mover-select" className="text-xs text-muted-foreground">Employee who moved it</label>
                <select id="foh-move-mover-select" data-testid="foh-move-mover-select" disabled={busy || candidates === null}
                  className="min-h-11 w-full rounded-md border bg-background px-2 text-sm"
                  value={mover.moverUserId ?? ''}
                  onChange={(e) => setMover({ kind: 'onBehalf', moverUserId: e.target.value || null })}>
                  <option value="">{candidates === null ? 'Reading employees…' : 'Choose the employee'}</option>
                  {(candidates ?? []).map((c) => (
                    <option key={c.userId} value={c.userId}>
                      {(c.displayName ?? 'Unnamed employee')} — {MOVER_ROLE_WORDS[c.moverRole] ?? c.moverRole}
                    </option>
                  ))}
                </select>
                {candidatesError && (
                  <p role="alert" className="text-xs text-destructive">Could not read the employee list. Nothing was moved.</p>
                )}
              </div>
            )}
          </fieldset>
        )}
        {loading && !ctx && <p className="text-sm text-muted-foreground">Reading the shop floor…</p>}

        {!confirming && ctx && groups.map((g) => {
          const items = dests.filter((d) => d.group === g);
          if (items.length === 0) return null;
          return (
            <section key={g} className="space-y-1.5" aria-label={GROUP_LABEL[g]}>
              <h3 className="text-sm font-medium">{GROUP_LABEL[g]}</h3>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="radiogroup" aria-label={GROUP_LABEL[g]}>
                {items.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    role="radio"
                    aria-checked={selected === d.key}
                    disabled={!!d.disabledReason || busy}
                    data-testid={`foh-move-dest-${d.key}`}
                    onClick={() => setSelected(d.key)}
                    className={cn(
                      'min-h-11 rounded-md border p-2 text-left text-sm transition-colors',
                      selected === d.key ? 'border-primary bg-primary/10' : 'hover:bg-muted',
                      d.disabledReason && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span className="block font-medium">{d.label}</span>
                    <span className="block text-xs text-muted-foreground">{d.disabledReason ?? 'Free'}</span>
                  </button>
                ))}
              </div>
            </section>
          );
        })}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {confirming ? (
            <>
              <Button variant="outline" className="min-h-11" onClick={() => setConfirming(false)} disabled={busy}>Back</Button>
              <Button className="min-h-11" onClick={() => { void submit(); }} disabled={busy || !chosen || !attestation} data-testid="foh-move-confirm">
                {busy ? 'Moving…' : 'Move vehicle'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="min-h-11" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button className="min-h-11" onClick={() => setConfirming(true)} disabled={!chosen || busy} data-testid="foh-move-next">
                Next
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
